if (!customElements.get('subscribe-navigation')) {
  customElements.define(
    'subscribe-navigation',
    class SubscribeNavigation extends HTMLElement {
      connectedCallback() {
        this.initializationFrame = requestAnimationFrame(() => this.initialize());
      }

      initialize() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.stickySection = this.closest('.shopify-section');
        this.nav = this.querySelector('nav');
        this.header = document.querySelector('.header-section header');
        this.originalMargins = new Map();
        if (!this.stickySection || !this.nav) return;

        // Stick the outer Shopify section so the short Custom Liquid wrapper does not constrain it.
        this.stickySection.classList.add('subscribe-navigation-section');
        this.refreshTargets();

        this.addEventListener('click', (event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const link = event.target.closest('a[href^="#"]');
          const entry = this.entries.find((item) => item.link === link);
          if (!entry) return;
          event.preventDefault();
          if (window.location.hash !== link.hash) history.pushState(null, '', link.hash);
          this.scrollToEntry(entry);
        }, { signal });

        window.addEventListener('scroll', () => {
          this.scheduleUpdate();
          if (this.pendingEntry) this.scheduleScrollEnd();
        }, { passive: true, signal });
        window.addEventListener('resize', () => this.scheduleUpdate(), { passive: true, signal });
        window.addEventListener('hashchange', () => this.followHash(), { signal });
        window.addEventListener('wheel', () => this.cancelPendingScroll(), { passive: true, signal });
        window.addEventListener('touchstart', () => this.cancelPendingScroll(), { passive: true, signal });
        window.addEventListener('keydown', (event) => {
          if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
            this.cancelPendingScroll();
          }
        }, { signal });
        document.addEventListener('shopify:section:reorder', () => {
          this.refreshTargets();
          this.scheduleUpdate();
        }, { signal });

        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        [this.stickySection, this.header, this.closest('main')].filter(Boolean).forEach((element) => {
          this.resizeObserver.observe(element);
        });
        this.headerObserver = new MutationObserver(() => this.scheduleUpdate());
        this.headerObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        if (this.header?.closest('.header-section')) {
          this.headerObserver.observe(this.header.closest('.header-section'), {
            attributes: true,
            attributeFilter: ['class'],
          });
        }

        this.update();
        this.followHash();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.initializationFrame);
        cancelAnimationFrame(this.updateFrame);
        this.updateFrame = null;
        clearTimeout(this.scrollEndTimer);
        this.pendingEntry = null;
        this.activeLink = null;
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        this.headerObserver?.disconnect();
        this.stickySection?.classList.remove('subscribe-navigation-section');
        this.stickySection?.style.removeProperty('--subscribe-header-offset');
        this.originalMargins?.forEach((margin, target) => {
          target.style.scrollMarginTop = margin;
        });
      }

      refreshTargets() {
        this.entries = [...this.querySelectorAll('a[href^="#"]')].map((link) => ({
          link,
          target: document.getElementById(decodeURIComponent(link.hash.slice(1))),
        })).filter((entry) => entry.target);
        // The menu's visual order can differ from the order of the sections on the page.
        this.entries.sort((a, b) => {
          return a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        });
      }

      getHeaderOffset() {
        if (!this.header?.matches('[is="sticky-header"]')) return 0;
        const alwaysVisible = this.header.dataset.stickyType === 'always';
        const pinned = document.body.classList.contains('header-pinned');
        return alwaysVisible || pinned ? Math.round(this.header.getBoundingClientRect().height) : 0;
      }

      getScrollOffset() {
        return this.getHeaderOffset() + this.stickySection.getBoundingClientRect().height + 16;
      }

      scheduleUpdate() {
        if (this.updateFrame) return;
        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.update();
        });
      }

      update() {
        const headerOffset = `${this.getHeaderOffset()}px`;
        if (this.stickySection.style.getPropertyValue('--subscribe-header-offset') !== headerOffset) {
          this.stickySection.style.setProperty('--subscribe-header-offset', headerOffset);
        }
        const scrollOffset = this.getScrollOffset();
        this.entries.forEach(({ target }) => {
          if (!this.originalMargins.has(target)) this.originalMargins.set(target, target.style.scrollMarginTop);
          target.style.scrollMarginTop = `${scrollOffset}px`;
        });
        let active = null;
        for (const entry of this.entries) {
          if (entry.target.getBoundingClientRect().top <= scrollOffset + 2) active = entry;
        }
        this.setActive(this.pendingEntry || active);
      }

      setActive(entry) {
        this.entries.forEach(({ link }) => {
          if (link === entry?.link) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
        if (this.activeLink === entry?.link) return;
        this.activeLink = entry?.link;
        if (!this.activeLink) return;
        const bounds = this.nav.getBoundingClientRect();
        const linkBounds = this.activeLink.getBoundingClientRect();
        if (linkBounds.left < bounds.left + 12 || linkBounds.right > bounds.right - 12) {
          // Scroll only the horizontal menu; scrollIntoView can also move the page vertically.
          this.nav.scrollTo({
            left: this.nav.scrollLeft + linkBounds.left - bounds.left - (bounds.width - linkBounds.width) / 2,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
      }

      scrollToEntry(entry, behavior) {
        this.pendingEntry = entry;
        this.setActive(entry);
        window.scrollTo({
          top: Math.max(0, window.scrollY + entry.target.getBoundingClientRect().top - this.getScrollOffset()),
          behavior: behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'),
        });
        this.scheduleScrollEnd();
      }

      scheduleScrollEnd() {
        clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = setTimeout(() => {
          if (this.pendingEntry) {
            // A header that appears on upward scroll can change height during the animation.
            const difference = this.pendingEntry.target.getBoundingClientRect().top - this.getScrollOffset();
            if (Math.abs(difference) > 2) window.scrollTo({ top: window.scrollY + difference, behavior: 'instant' });
          }
          this.pendingEntry = null;
          this.scheduleUpdate();
        }, 180);
      }

      cancelPendingScroll() {
        this.pendingEntry = null;
        clearTimeout(this.scrollEndTimer);
        this.scheduleUpdate();
      }

      followHash() {
        const entry = this.entries.find(({ link }) => link.hash === window.location.hash);
        if (entry) this.scrollToEntry(entry, 'instant');
      }
    }
  );
}
